'use client';

import clsx from 'clsx';
import {
  type UseRefinementListProps,
  useInstantSearch,
  useRefinementList,
} from 'react-instantsearch';

import Collapsible from '@commons/components/Collapsible';
import Checkbox from '@commons/components/Form/CheckBox';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import { generateId } from '@commons/helpers/string';

import styles from './DropdownFilter.module.scss';

// Type definition for the DropdownFilter component properties.

type Props = UseRefinementListProps & {
  label: string;
  defaultOpen?: boolean;
};

/**
 * DropdownFilter component.
 *
 * Renders a refinement list widget to filter an attribute as a dropdown.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/refinement-list/react/
 */

const DropdownFilter = (props: Props) => {
  const { indexUiState } = useInstantSearch();

  const {
    label,
    limit = 20,
    attribute,
    operator = 'or',
    sortBy = ['name:asc'],
    ...refinementListProps
  } = props;

  // Get the attribute default items from the index UI state (set by the routing) to determine if the dropdown should be open by default.
  const defaultItems = indexUiState.refinementList?.[attribute];
  const defaultOpen = props.defaultOpen || (defaultItems && defaultItems.length > 0);

  const { items, refine } = useRefinementList({
    limit,
    sortBy,
    operator,
    attribute,
    ...refinementListProps,
  });

  if (!items.length) return null;

  const renderedLabel = (
    <Text level="body-md" weight="medium" marginBottom="none">
      {label}
    </Text>
  );

  const renderedItems = items.map(({ label, value, isRefined }) => {
    const id = generateId(`${attribute}-${value}`);
    return (
      <Checkbox
        key={id}
        name={id}
        checked={isRefined}
        onClick={() => refine(value)}
        className={clsx(styles.item, isRefined && styles.active)}
      >
        <Text level="body-sm" marginBottom="none">
          {label}
        </Text>
      </Checkbox>
    );
  });

  return (
    <Collapsible gap="xs" title={renderedLabel} hasBorderTop={false} defaultOpen={defaultOpen}>
      <Flex gap={8} direction="column">
        {renderedItems}
      </Flex>
    </Collapsible>
  );
};

export default DropdownFilter;
